import { CollectionTable, CollectionCards } from '../../app/collection-view.js';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  CurrencyInput,
  DateRangePicker,
  FormErrorSummary,
  HelpTip,
  IconButton,
  Input,
  Modal,
  Select,
  StatusPill,
  Switch,
  Textarea,
  formatCivilRange,
  formatCurrency,
  todayCivilDate,
  useSnackbar,
} from '@hue/design-system';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { Boxes, PencilLine, Plus, RotateCcw, ShoppingBag, Trash2, Archive } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import {
  getApiError,
  inventoryApi,
  type InventoryBaseUnit,
  type InventoryLotTracking,
  type InventoryProduct,
  type InventoryProductSort,
} from '../../lib/api.js';
import { formatDate } from '../../lib/time-zone.js';
import { eanWarning, generateEan13, type EanPrefix } from './ean.js';
import { EanGenerateButton } from './ean-generate-button.js';
import {
  EmptyCard,
  ErrorCard,
  FilterBar,
  FilterGroup,
  LoadingCard,
  Pagination,
  RequestAlert,
  SortBar,
  SortableColumn,
} from '../../app/data-view.js';
import type { ProductsView } from '../../app/list-views.js';
import type { ViewSetter } from '../../app/view-state.js';
import {
  BASE_QUANTITY,
  activityOptions,
  baseUnitLabels,
  baseUnitOptions,
  lotFilterOptions,
  lotTrackingLabels,
  lotTrackingOptions,
  shortUnit,
  trimQuantity,
  type InventoryCapabilities,
} from './shared.js';

const packageSchema = z.object({
  id: z.string().nullable(),
  displayName: z
    .string()
    .trim()
    .min(2, 'Informe o nome da apresentação.')
    .max(120, 'Use até 120 caracteres.'),
  baseQuantity: z
    .string()
    .trim()
    .refine(
      (value) => BASE_QUANTITY.test(value) && Number(value.replace(',', '.')) > 0,
      'Informe uma conversão positiva, com até 6 casas decimais.',
    ),
  salePriceCents: z.number().int().min(0).nullable(),
  barcode: z
    .string()
    .trim()
    .regex(/^[0-9A-Za-z.\-/+]{0,64}$/u, 'Use até 64 letras, números ou . - / +, sem espaço.'),
});
const productSchema = z.object({
  displayName: z
    .string()
    .trim()
    .min(2, 'Informe o nome do produto.')
    .max(160, 'Use até 160 caracteres.'),
  description: z.string().trim().max(1000, 'Use até 1000 caracteres.'),
  sku: z.string().trim().max(80, 'Use até 80 caracteres.'),
  baseUnit: z.enum(['UNIT', 'MILLILITER', 'GRAM']),
  lotTracking: z.enum(['NONE', 'REQUIRED']),
  sellable: z.boolean(),
  packages: z
    .array(packageSchema)
    .max(50, 'Use até 50 apresentações.')
    .superRefine((items, context) => {
      const seen = new Map<string, number>();
      items.forEach((item, index) => {
        const key = item.displayName.trim().toLocaleLowerCase('pt-BR');
        if (seen.has(key)) {
          context.addIssue({
            code: 'custom',
            path: [index, 'displayName'],
            message: 'Essa apresentação está repetida.',
          });
        }
        seen.set(key, index);
      });
    }),
});
type ProductForm = z.infer<typeof productSchema>;

/// Vendável precisa de preço em ao menos uma apresentação — a mesma regra do domínio, avisada
/// antes de mandar. As apresentações sem preço continuam só de compra e consumo.
const productFormSchema = productSchema.superRefine((data, context) => {
  if (data.sellable && !data.packages.some((item) => item.salePriceCents !== null)) {
    context.addIssue({
      code: 'custom',
      path: ['sellable'],
      message: 'Informe o preço de venda de ao menos uma apresentação.',
    });
  }
});

const columns: Array<{ sort: InventoryProductSort; label: string }> = [
  { sort: 'displayName', label: 'Produto' },
  { sort: 'sku', label: 'SKU' },
  { sort: 'createdAt', label: 'Cadastrado em' },
];

export function ProductsPanel({
  capabilities,
  timeZone,
  view,
  onView,
}: {
  capabilities: InventoryCapabilities;
  timeZone: string;
  view: ProductsView;
  onView: ViewSetter<ProductsView>;
}) {
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const [createOpen, setCreateOpen] = useState(false);
  const [activationTarget, setActivationTarget] = useState<InventoryProduct>();
  const [activationError, setActivationError] = useState<string>();
  const filters = {
    ...(view.search.trim() ? { search: view.search.trim() } : {}),
    activity: view.activity,
    lotTracking: view.lotTracking,
    ...(view.from ? { from: view.from } : {}),
    ...(view.to ? { to: view.to } : {}),
    sort: view.sort,
    direction: view.direction,
    limit: view.limit,
    offset: view.offset,
  };
  const page = useQuery({
    queryKey: ['company', 'inventory', 'products', filters],
    queryFn: () => inventoryApi.listProducts(filters),
    placeholderData: keepPreviousData,
    retry: false,
  });
  const detail = useQuery({
    queryKey: ['company', 'inventory', 'products', view.product],
    queryFn: () => inventoryApi.getProduct(view.product!),
    enabled: Boolean(view.product),
    retry: false,
  });

  function toggleSort(column: InventoryProductSort) {
    if (view.sort === column) onView({ direction: view.direction === 'asc' ? 'desc' : 'asc' });
    else onView({ sort: column, direction: column === 'createdAt' ? 'desc' : 'asc' });
  }

  async function setActive(product: InventoryProduct) {
    setActivationError(undefined);
    try {
      await inventoryApi.setProductActive(product.id, !product.active);
      await queryClient.invalidateQueries({ queryKey: ['company', 'inventory'] });
      setActivationTarget(undefined);
      snackbar.success(product.active ? 'Produto arquivado.' : 'Produto reativado.');
    } catch (error) {
      setActivationError(getApiError(error));
    }
  }

  const total = page.data?.total ?? 0;
  const chips = [
    ...(view.activity === 'ALL'
      ? []
      : [
          {
            key: 'activity',
            label: activityOptions.find((item) => item.value === view.activity)!.label,
            onRemove: () => onView({ activity: 'ALL' }),
          },
        ]),
    ...(view.lotTracking === 'ALL'
      ? []
      : [
          {
            key: 'lotTracking',
            label: lotFilterOptions.find((item) => item.value === view.lotTracking)!.label,
            onRemove: () => onView({ lotTracking: 'ALL' }),
          },
        ]),
    ...((view.from ?? view.to)
      ? [
          {
            key: 'range',
            label: formatCivilRange({ from: view.from, to: view.to }, 'Qualquer dia'),
            onRemove: () => onView({ from: null, to: null }),
          },
        ]
      : []),
  ];

  return (
    <>
      <div className="mb-5 flex justify-end">
        {capabilities.canManageProducts ? (
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="size-4" />
            Cadastrar produto
          </Button>
        ) : null}
      </div>

      <FilterBar
        searchId="inventory-search"
        searchLabel="Buscar produto"
        searchPlaceholder="Nome, SKU ou descrição"
        search={view.search}
        onSearchChange={(search) => onView({ search }, { replace: true })}
        chips={chips}
        onClear={() => onView({ activity: 'ALL', lotTracking: 'ALL', from: null, to: null })}
        count={`${total} ${total === 1 ? 'produto' : 'produtos'}`}
        resultsLabel={`Ver ${total} ${total === 1 ? 'produto' : 'produtos'}`}
        pageSize={view.limit}
        onPageSizeChange={(limit) => onView({ limit })}
      >
        <FilterGroup
          label="Situação"
          description="Filtrar produtos por situação"
          options={activityOptions}
          value={view.activity}
          onChange={(activity) => onView({ activity })}
        />
        <FilterGroup
          label="Controle por lote"
          description="Filtrar produtos por controle de lote"
          options={lotFilterOptions}
          value={view.lotTracking}
          onChange={(lotTracking) => onView({ lotTracking })}
        />
        <div className="min-w-0">
          <span className="mb-1.5 block text-xs font-semibold text-content/70">Cadastro</span>
          <DateRangePicker
            label="Dias de cadastro"
            value={{ from: view.from, to: view.to }}
            onValueChange={(next) => onView({ from: next.from, to: next.to })}
            today={todayCivilDate(timeZone)}
          />
        </div>
      </FilterBar>

      {page.isLoading ? (
        <LoadingCard label="Carregando produtos" />
      ) : page.error ? (
        <ErrorCard message={getApiError(page.error)} />
      ) : total === 0 ? (
        <EmptyCard
          icon={Boxes}
          title="Nenhum produto encontrado"
          description="Ajuste os filtros ou cadastre o primeiro produto do estoque."
        />
      ) : (
        <div className="hue-responsive-collection overflow-hidden rounded-2xl border border-surface-border bg-surface-card shadow-soft">
          <CollectionCards>
            <div className="hue-collection-cards">
              <SortBar
                label="Ordenação dos produtos"
                columns={columns}
                sort={view.sort}
                direction={view.direction}
                onToggle={toggleSort}
              />
              <ul className="divide-y divide-surface-border">
                {page.data?.products.map((product) => (
                  <li key={product.id} className="p-4">
                    <div className="flex items-start gap-3">
                      <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-brand-500/10 text-brand-400">
                        <Boxes className="size-5" aria-hidden="true" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <strong className="block truncate text-sm">{product.displayName}</strong>
                        <span className="mt-1 block truncate text-xs text-content-muted">
                          {product.sku ?? 'Sem SKU'}
                        </span>
                        <div className="mt-2 flex flex-wrap gap-2 text-xs text-content-muted">
                          <span>{baseUnitLabels[product.baseUnit]}</span>
                          <span>{lotTrackingLabels[product.lotTracking]}</span>
                          <span>{packageSummary(product)}</span>
                        </div>
                        {product.active && !product.sellable ? null : (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {product.sellable ? (
                              <StatusPill tone="brand">Vendável</StatusPill>
                            ) : null}
                            {product.active ? null : (
                              <StatusPill tone="neutral">Arquivado</StatusPill>
                            )}
                          </div>
                        )}
                      </div>
                      <Actions
                        product={product}
                        capabilities={capabilities}
                        onEdit={() => onView({ product: product.id })}
                        onActivation={() => setActivationTarget(product)}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </CollectionCards>
          <CollectionTable>
            <table className="w-full table-fixed border-collapse text-left">
              <caption className="sr-only">Produtos do estoque da empresa</caption>
              <thead>
                <tr className="border-b border-surface-border">
                  {columns.map((column) => (
                    <SortableColumn
                      key={column.sort}
                      column={column.sort}
                      label={column.label}
                      sort={view.sort}
                      direction={view.direction}
                      onToggle={toggleSort}
                    />
                  ))}
                  <th scope="col" className="px-5 py-3 text-xs font-semibold text-content/70">
                    Unidade-base
                  </th>
                  <th scope="col" className="px-5 py-3 text-xs font-semibold text-content/70">
                    Apresentações
                  </th>
                  <th className="w-32">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-border">
                {page.data?.products.map((product) => (
                  <tr key={product.id}>
                    <td className="px-5 py-4">
                      <strong className="block truncate text-sm">{product.displayName}</strong>
                      <span className="hue-list-description mt-1 block truncate text-xs text-content-muted">
                        {product.description ?? 'Sem descrição'}
                      </span>
                      {product.sellable ? <StatusPill tone="brand">Vendável</StatusPill> : null}
                      {product.active ? null : <StatusPill tone="neutral">Arquivado</StatusPill>}
                    </td>
                    <td className="px-5 py-4 text-xs text-content-muted">
                      {product.sku ?? 'Sem SKU'}
                    </td>
                    <td className="px-5 py-4 text-xs text-content-muted">
                      {formatDate(product.createdAt, timeZone)}
                    </td>
                    <td className="px-5 py-4 text-xs text-content-muted">
                      <span className="block">{baseUnitLabels[product.baseUnit]}</span>
                      <span className="block">{lotTrackingLabels[product.lotTracking]}</span>
                    </td>
                    <td className="px-5 py-4 text-xs text-content-muted">
                      {packageSummary(product)}
                    </td>
                    <td className="px-3 py-4">
                      <Actions
                        product={product}
                        capabilities={capabilities}
                        onEdit={() => onView({ product: product.id })}
                        onActivation={() => setActivationTarget(product)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CollectionTable>
          <Pagination
            total={total}
            limit={view.limit}
            offset={view.offset}
            onOffsetChange={(offset) => onView({ offset })}
            itemLabel={(count) => (count === 1 ? 'produto' : 'produtos')}
          />
        </div>
      )}

      <ProductDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        product={null}
        capabilities={capabilities}
        onSaved={async () => {
          await queryClient.invalidateQueries({ queryKey: ['company', 'inventory'] });
          setCreateOpen(false);
          snackbar.success('Produto cadastrado.');
        }}
      />
      <ProductDialog
        open={Boolean(view.product)}
        onOpenChange={(open) => {
          if (!open) onView({ product: undefined });
        }}
        product={detail.data ?? null}
        loading={detail.isLoading}
        {...(detail.error ? { error: getApiError(detail.error) } : {})}
        capabilities={capabilities}
        onSaved={async () => {
          await queryClient.invalidateQueries({ queryKey: ['company', 'inventory'] });
          onView({ product: undefined });
          snackbar.success('Produto atualizado.');
        }}
      />
      {activationTarget ? (
        <Modal
          open
          onOpenChange={() => setActivationTarget(undefined)}
          title={activationTarget.active ? 'Arquivar produto' : 'Reativar produto'}
          description={
            activationTarget.active
              ? 'O produto sai da operação, mas o cadastro e o histórico permanecem.'
              : 'O produto volta a ficar disponível para a operação.'
          }
        >
          <p className="text-sm">
            {activationTarget.active ? 'Arquivar' : 'Reativar'}{' '}
            <strong>{activationTarget.displayName}</strong>?
          </p>
          {activationError ? <RequestAlert message={activationError} /> : null}
          <div className="mt-7 flex justify-end gap-3 border-t border-surface-border pt-5">
            <Button variant="outline" onClick={() => setActivationTarget(undefined)}>
              Cancelar
            </Button>
            <Button onClick={() => void setActive(activationTarget)}>
              {activationTarget.active ? 'Arquivar produto' : 'Reativar produto'}
            </Button>
          </div>
        </Modal>
      ) : null}
    </>
  );
}

function packageSummary(product: InventoryProduct): string {
  const active = product.packages.filter((item) => item.active);
  if (active.length === 0) return 'Sem apresentação';
  return active
    .map(
      (item) =>
        `${item.displayName} · ${trimQuantity(item.baseQuantity)}` +
        (product.sellable && item.salePriceCents !== null
          ? ` · ${formatCurrency(item.salePriceCents, 'BRL')}`
          : ''),
    )
    .join(', ');
}

/// Vende-se o produto marcado como vendável que tem ao menos uma apresentação com preço.
function sellableNow(product: InventoryProduct): boolean {
  return (
    product.active &&
    product.sellable &&
    product.packages.some((item) => item.active && item.salePriceCents !== null)
  );
}

function Actions({
  product,
  capabilities,
  onEdit,
  onActivation,
}: {
  product: InventoryProduct;
  capabilities: InventoryCapabilities;
  onEdit: () => void;
  onActivation: () => void;
}) {
  const { onSell } = capabilities;
  const sell = onSell && sellableNow(product);
  if (!capabilities.canManageProducts && !sell) return null;
  return (
    <div className="flex justify-end gap-1">
      {sell ? (
        <IconButton
          label={`Vender ${product.displayName}`}
          icon={<ShoppingBag className="size-4" />}
          onClick={() => onSell(product)}
        />
      ) : null}
      {capabilities.canManageProducts ? (
        <>
          <IconButton
            label="Editar produto"
            icon={<PencilLine className="size-4" />}
            onClick={onEdit}
          />
          <IconButton
            label={product.active ? 'Arquivar' : 'Reativar'}
            {...(product.active ? { tone: 'danger' as const } : {})}
            icon={
              product.active ? <Archive className="size-4" /> : <RotateCcw className="size-4" />
            }
            onClick={onActivation}
          />
        </>
      ) : null}
    </div>
  );
}

function ProductDialog({
  open,
  onOpenChange,
  product,
  loading = false,
  error,
  capabilities,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: InventoryProduct | null;
  loading?: boolean;
  error?: string;
  capabilities: InventoryCapabilities;
  onSaved: () => Promise<void>;
}) {
  const [requestError, setRequestError] = useState<string>();
  const form = useForm<ProductForm>({
    resolver: zodResolver(productFormSchema),
    defaultValues: emptyProduct(),
  });
  const packages = useFieldArray({ control: form.control, name: 'packages' });
  const baseUnit = useWatch({ control: form.control, name: 'baseUnit' });
  const lotTracking = useWatch({ control: form.control, name: 'lotTracking' });
  const sellable = useWatch({ control: form.control, name: 'sellable' });
  const packagePrices = useWatch({ control: form.control, name: 'packages' });
  /// Sorteia até não repetir outra apresentação do formulário; a repetição com o resto da empresa,
  /// remota com 9 ou 11 números sorteados, é recusada pelo servidor como qualquer EAN repetido.
  const generateEan = (index: number, prefix: EanPrefix) => {
    const taken = new Set(packagePrices.map((item) => item.barcode.trim()));
    let code = generateEan13(prefix);
    while (taken.has(code)) code = generateEan13(prefix);
    form.setValue(`packages.${index}.barcode`, code, {
      shouldDirty: true,
      shouldValidate: form.formState.isSubmitted,
    });
  };
  useEffect(() => {
    if (open) form.reset(product ? formOf(product) : emptyProduct());
  }, [form, open, product]);

  /// Fechar o diálogo apaga a falha do pedido anterior: reabrir para tentar de novo não pode
  /// começar mostrando o erro da tentativa passada.
  function changeOpen(next: boolean) {
    if (!next) setRequestError(undefined);
    onOpenChange(next);
  }

  async function save(data: ProductForm) {
    setRequestError(undefined);
    const revision = {
      displayName: data.displayName,
      description: data.description || null,
      sku: data.sku || null,
      lotTracking: data.lotTracking,
      sellable: data.sellable,
      packages: data.packages.map((item) => ({
        id: item.id,
        displayName: item.displayName,
        baseQuantity: item.baseQuantity.replace(',', '.'),
        salePriceCents: item.salePriceCents,
        barcode: item.barcode.trim() || null,
      })),
    };
    try {
      if (product) await inventoryApi.updateProduct(product.id, revision);
      else await inventoryApi.createProduct({ ...revision, baseUnit: data.baseUnit });
      await onSaved();
    } catch (cause) {
      setRequestError(getApiError(cause));
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={changeOpen}
      title={product ? 'Editar produto' : 'Cadastrar produto'}
      description="A unidade-base define como saldo e consumo serão contados; as apresentações só convertem a compra para essa unidade."
    >
      {loading ? (
        <LoadingCard label="Carregando produto" />
      ) : error ? (
        <ErrorCard message={error} />
      ) : (
        <form noValidate onSubmit={(event) => void form.handleSubmit(save)(event)}>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Input
                label="Nome do produto"
                error={form.formState.errors.displayName?.message}
                {...form.register('displayName')}
              />
            </div>
            <Input
              label="SKU (opcional)"
              hint="Código que a empresa usa para identificar o produto."
              error={form.formState.errors.sku?.message}
              {...form.register('sku')}
            />
            {product ? (
              <Input
                label="Unidade-base"
                value={baseUnitLabels[product.baseUnit]}
                readOnly
                disabled
                hint="A unidade-base não muda depois do cadastro: ela é a régua do saldo já registrado."
              />
            ) : (
              <Select
                label="Unidade-base"
                value={baseUnit}
                options={baseUnitOptions}
                onValueChange={(value) =>
                  form.setValue('baseUnit', value as InventoryBaseUnit, { shouldValidate: true })
                }
              />
            )}
            <div className="sm:col-span-2">
              <Select
                label="Controle por lote"
                labelAddon={
                  <HelpTip
                    label="Como funciona o controle por lote"
                    content="Com lote obrigatório, toda entrada, perda, ajuste e transferência pede o lote, e o saldo fica separado por lote e validade. Use para produtos com validade ou que precisam ser rastreados. Sem lote obrigatório, o saldo é um só e as movimentações não pedem lote."
                  />
                }
                value={lotTracking}
                options={lotTrackingOptions}
                onValueChange={(value) =>
                  form.setValue('lotTracking', value as InventoryLotTracking, {
                    shouldValidate: true,
                  })
                }
              />
            </div>
            <div className="sm:col-span-2">
              <Textarea
                label="Descrição"
                rows={3}
                error={form.formState.errors.description?.message}
                {...form.register('description')}
              />
            </div>
          </div>

          <fieldset className="mt-6 border-t border-surface-border pt-5">
            <legend className="text-sm font-semibold">Apresentações físicas</legend>
            <p className="mb-3 mt-1 text-xs text-content-muted">
              Quanto da unidade-base cabe em cada embalagem, como “frasco de 500 ml”. Fornecedor e
              preço de compra pertencem a Compras, não ao produto.
            </p>
            <div className="mb-4 rounded-xl border border-surface-border p-3">
              <Switch
                checked={sellable}
                onChange={(next) =>
                  form.setValue('sellable', next, { shouldValidate: form.formState.isSubmitted })
                }
              >
                <span>
                  <span className="block font-semibold">Vendável</span>
                  <span className="block text-xs text-content-muted">
                    Aparece em Vendas pelo preço de venda de cada apresentação. Desligado, o produto
                    é só insumo.
                  </span>
                </span>
              </Switch>
              {form.formState.errors.sellable?.message ? (
                <p role="alert" className="mt-2 text-xs text-danger">
                  {form.formState.errors.sellable.message}
                </p>
              ) : null}
            </div>
            {packages.fields.length === 0 ? (
              <p className="mb-3 text-xs text-content-faint">
                Nenhuma apresentação. O produto pode existir só com a unidade-base.
              </p>
            ) : null}
            <ul className="grid gap-3">
              {packages.fields.map((field, index) => (
                <li
                  key={field.id}
                  className="grid gap-2 rounded-xl border border-surface-border p-3"
                >
                  <div className="flex items-end gap-2">
                    <div className="min-w-0 flex-1">
                      <Input
                        label="Apresentação"
                        placeholder="Frasco 500 ml"
                        error={form.formState.errors.packages?.[index]?.displayName?.message}
                        {...form.register(`packages.${index}.displayName`)}
                      />
                    </div>
                    <div className="w-28 shrink-0">
                      <Input
                        label={`Em ${shortUnit(product?.baseUnit ?? baseUnit)}`}
                        inputMode="decimal"
                        error={form.formState.errors.packages?.[index]?.baseQuantity?.message}
                        {...form.register(`packages.${index}.baseQuantity`)}
                      />
                    </div>
                    <IconButton
                      label={`Remover apresentação ${index + 1}`}
                      tone="danger"
                      icon={<Trash2 className="size-4" />}
                      onClick={() => packages.remove(index)}
                    />
                  </div>
                  <div className="flex flex-wrap items-end gap-2">
                    <div className="min-w-40 flex-1">
                      <Input
                        label="EAN (opcional)"
                        placeholder="Leia, digite ou gere"
                        inputMode="numeric"
                        error={form.formState.errors.packages?.[index]?.barcode?.message}
                        warning={eanWarning(packagePrices[index]?.barcode)}
                        action={
                          <EanGenerateButton
                            label={`Gerar EAN da apresentação ${index + 1}`}
                            onGenerate={(prefix) => generateEan(index, prefix)}
                          />
                        }
                        {...form.register(`packages.${index}.barcode`)}
                      />
                    </div>
                    {sellable ? (
                      <div className="w-36 shrink-0">
                        <CurrencyInput
                          label="Preço de venda"
                          currency="BRL"
                          value={packagePrices[index]?.salePriceCents ?? null}
                          onValueChange={(value) =>
                            form.setValue(`packages.${index}.salePriceCents`, value, {
                              shouldValidate: form.formState.isSubmitted,
                            })
                          }
                        />
                      </div>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
            <Button
              type="button"
              variant="outline"
              className="mt-3"
              onClick={() =>
                packages.append({
                  id: null,
                  displayName: '',
                  baseQuantity: '',
                  salePriceCents: null,
                  barcode: '',
                })
              }
            >
              <Plus className="size-4" />
              Adicionar apresentação
            </Button>
          </fieldset>

          {requestError ? <RequestAlert message={requestError} /> : null}
          {capabilities.canManageProducts ? (
            <div className="mt-6 flex flex-wrap items-center justify-end gap-3 border-t border-surface-border pt-5">
              <FormErrorSummary className="basis-full sm:basis-auto sm:flex-1" />
              <Button type="button" variant="outline" onClick={() => changeOpen(false)}>
                Cancelar
              </Button>
              <Button loading={form.formState.isSubmitting}>
                {product ? 'Salvar produto' : 'Cadastrar produto'}
              </Button>
            </div>
          ) : null}
        </form>
      )}
    </Modal>
  );
}

function emptyProduct(): ProductForm {
  return {
    displayName: '',
    description: '',
    sku: '',
    baseUnit: 'UNIT',
    lotTracking: 'NONE',
    sellable: false,
    packages: [],
  };
}

function formOf(product: InventoryProduct): ProductForm {
  return {
    displayName: product.displayName,
    description: product.description ?? '',
    sku: product.sku ?? '',
    baseUnit: product.baseUnit,
    lotTracking: product.lotTracking,
    sellable: product.sellable,
    packages: product.packages
      .filter((item) => item.active)
      .map((item) => ({
        id: item.id,
        displayName: item.displayName,
        baseQuantity: trimQuantity(item.baseQuantity),
        salePriceCents: item.salePriceCents,
        barcode: item.barcode ?? '',
      })),
  };
}

/// Catálogo fechado: uma empresa tem punhados de locais, não milhares. Não paginar é decisão
